import type { ServiceRequestStatus } from '../types/serviceRequestListType';

/**
 * Statuses from which an SR can be rescheduled. Mirrors the BE
 * `ReschedulePolicyService.RESCHEDULABLE_FROM` allow-list.
 *
 * `unfulfilled` is reschedulable (RR-622): once a technician reports a missed
 * visit, the admin can put the SR back on the calendar. Like `delayed` it is
 * waived from the 24h lead-time rule (see {@link GATED_24H}).
 */
export const RESCHEDULABLE_FROM: readonly ServiceRequestStatus[] = [
  'assigned',
  'accepted',
  'rejected',
  'delayed',
  'unfulfilled',
];

/**
 * Narrower reschedulable-status list for the *assignee* (the technician/
 * admin the SR is currently assigned to) when they are not also the
 * assigner — they may only reschedule while the SR is `rejected`. The
 * assigner and Super Admin keep the full {@link RESCHEDULABLE_FROM} list.
 */
export const ASSIGNEE_RESCHEDULABLE_FROM: readonly ServiceRequestStatus[] = [
  'rejected',
];

/**
 * Statuses subject to the 24h lead-time rule. `assigned`, `accepted`, and
 * `rejected` all still carry their *original* scheduled visit ahead of them
 * (a rejected SR keeps the proposed slot until it's rescheduled or
 * reassigned), so a near-term visit needs protecting in each case.
 * `delayed`/`unfulfilled` are waived by necessity, not just policy: their
 * `scheduled_start_at` is always in the past (that's what makes them
 * delayed/unfulfilled), so gating them here would make every such SR
 * permanently read as "within 24h" and never reschedulable.
 */
const GATED_24H: readonly ServiceRequestStatus[] = [
  'assigned',
  'accepted',
  'rejected',
];

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

export type RescheduleBlockReason = 'STATUS' | 'WITHIN_24H';

export interface RescheduleEligibility {
  canReschedule: boolean;
  reason?: RescheduleBlockReason;
}

/**
 * FE mirror of the BE 24h-lead-time + status allow-list gate.
 *
 * - Returns `STATUS` when the current status is not reschedulable at all.
 * - Returns `WITHIN_24H` only when status is gated AND `scheduledStartAt`
 *   is non-null AND the visit is < 24h away. Null `scheduled_start_at` is
 *   treated as "no prior visit to protect" (matches the BE belt-and-braces
 *   guard for REJECTED rows whose schedule was nulled).
 *
 * `allowedFrom` defaults to {@link RESCHEDULABLE_FROM} (the assigner/Super
 * Admin list) — pass {@link ASSIGNEE_RESCHEDULABLE_FROM} when the caller is
 * only the SR's current assignee, not also its assigner.
 *
 * Re-compute on every render — do not cache. BE is the final gate.
 */
export const isReschedulableNow = (
  status: ServiceRequestStatus | undefined,
  scheduledStartAt: string | null | undefined,
  now: number = Date.now(),
  allowedFrom: readonly ServiceRequestStatus[] = RESCHEDULABLE_FROM
): RescheduleEligibility => {
  if (!status || !allowedFrom.includes(status)) {
    return { canReschedule: false, reason: 'STATUS' };
  }
  if (!GATED_24H.includes(status)) {
    return { canReschedule: true };
  }
  if (!scheduledStartAt) {
    return { canReschedule: true };
  }
  const startMs = Date.parse(scheduledStartAt);
  if (Number.isNaN(startMs)) {
    return { canReschedule: true };
  }
  if (startMs - now < TWENTY_FOUR_HOURS_MS) {
    return { canReschedule: false, reason: 'WITHIN_24H' };
  }
  return { canReschedule: true };
};

/**
 * Statuses from which the *assigned technician* can reschedule an SR —
 * narrower than {@link RESCHEDULABLE_FROM}, mirroring the BE rule that a
 * technician-initiated reschedule requires the SR to currently be `rejected`.
 */
const TECHNICIAN_RESCHEDULABLE_FROM: readonly ServiceRequestStatus[] = [
  'rejected',
];

/**
 * FE mirror of the BE technician *self*-reschedule status gate — a distinct
 * flow from the admin-initiated {@link isReschedulableNow} above, with its
 * own rules. There is no time-based check here — the BE still enforces
 * "new slot ≥24h out" against the *new* window, which surfaces via the
 * generic `WITHIN_24H` error kind on submit.
 */
export const isTechnicianReschedulableNow = (
  status: ServiceRequestStatus | undefined
): RescheduleEligibility => {
  if (!status || !TECHNICIAN_RESCHEDULABLE_FROM.includes(status)) {
    return { canReschedule: false, reason: 'STATUS' };
  }
  return { canReschedule: true };
};
